import axios from 'axios';
import type { AuthAudience } from './types';

// Uses an un-interceptored axios instance so auth calls never accidentally
// try to attach a stale bearer / recursively refresh. Base URL matches the
// rest of the app (nginx proxies /api → backend).
const raw = axios.create({ baseURL: '/api', timeout: 30000 });

export interface TokenResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface ApiError {
  error: string;
  message: string;
}

// The backend serializes with snake_case (matches the rest of the API surface),
// but the tokenStorage / tokenManager code uses camelCase. Normalize here so
// the rest of the app sees a camelCased shape.
interface RawTokenResponse {
  access_token?: string;
  accessToken?: string;
  id_token?: string;
  idToken?: string;
  refresh_token?: string;
  refreshToken?: string;
  expires_in?: number;
  expiresIn?: number;
  token_type?: string;
  tokenType?: string;
}

function normalize(d: RawTokenResponse): TokenResponse {
  return {
    accessToken: d.access_token ?? d.accessToken ?? '',
    idToken: d.id_token ?? d.idToken ?? '',
    refreshToken: d.refresh_token ?? d.refreshToken ?? '',
    expiresIn: d.expires_in ?? d.expiresIn ?? 0,
    tokenType: d.token_type ?? d.tokenType ?? 'Bearer'
  };
}

export async function login(
  email: string,
  password: string,
  audience: AuthAudience = 'admin'
): Promise<TokenResponse> {
  const res = await raw.post<RawTokenResponse>(
    `/auth/login?audience=${audience}`,
    { email, password }
  );
  return normalize(res.data);
}

export async function refresh(
  refreshToken: string,
  audience: AuthAudience = 'admin'
): Promise<TokenResponse> {
  const res = await raw.post<RawTokenResponse>(
    `/auth/refresh?audience=${audience}`,
    { refresh_token: refreshToken }
  );
  return normalize(res.data);
}

export async function logout(
  accessToken: string | null,
  refreshToken: string | null,
  audience: AuthAudience = 'admin'
): Promise<void> {
  try {
    await raw.post(`/auth/logout?audience=${audience}`, {
      access_token: accessToken,
      refresh_token: refreshToken
    });
  } catch {
    // Revoke is best-effort. Even if it fails, we still clear local tokens.
  }
}

/** Local dev only: is the "sign in as …" switch available on this backend? */
export async function getDevLoginStatus(): Promise<{ enabled: boolean; roles: string[] }> {
  try {
    const res = await raw.get<{ enabled: boolean; roles: string[] }>('/auth/dev-login');
    return res.data;
  } catch {
    return { enabled: false, roles: [] };
  }
}

/** Local dev only: sign in as a fake user with the given role, no password. */
export async function devLogin(role: string): Promise<TokenResponse> {
  const res = await raw.post<RawTokenResponse>('/auth/dev-login', { role });
  return normalize(res.data);
}

export interface VerifyResponse {
  valid: boolean;
  claims: Record<string, unknown> | null;
}

export async function verifyToken(
  accessToken: string,
  audience: AuthAudience = 'admin'
): Promise<VerifyResponse> {
  const res = await raw.post<VerifyResponse>(
    `/auth/verify-token?audience=${audience}`,
    { access_token: accessToken }
  );
  return res.data;
}
